import React, { useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { NavLink, RouteComponentProps, withRouter } from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";
import styled from "styled-components";
import LogoIcon from "sweet-web/components/icons/LogoIcon";
import { colors } from "sweet-web/lib/styles";
import { RootState } from "sweet-web/redux";
import { LogOut } from "sweet-web/redux/app/actions";
import * as selectors from "sweet-web/redux/selectors";
import CreateListing from "./CreateListing";
import MobileMenu from "./MobileMenu";
import OpenButton from "./MobileMenu/OpenButton";
import Search from "./Search";

const Wrapper = styled.div<{ noBorder: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0 15px;
  background-color: ${colors.white};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  height: 60px;
  border-bottom: ${props => (props.noBorder ? 0 : 1)}px solid
    ${colors.lightGray};
`;

const LogoWrapper = styled.div`
  margin: 10px 0;
  padding-top: 2px;
  width: 30px;
  margin-right: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Left = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Right = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`;

const LinksWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 60px;
  @media screen and (max-width: 600px) {
    display: none;
  }
`;

const User = styled.div`
  outline: none;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: ${(props: { active: boolean }) =>
    props.active ? colors.primary : colors.gray};
  text-decoration: none;
  border-radius: 10px;
  margin-left: 15px;
  white-space: nowrap;
  display: flex;
  flex-direction: column;
  transition: all 150ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
  @media screen and (max-width: 600px) {
    display: none;
  }
  .dropdown {
    display: none;
  }
  &:hover {
    cursor: default;
    .dropdown {
      display: block;
    }
  }
`;

const StyledLink = styled(NavLink)`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.gray};
  text-decoration: none;
  margin: 0 10px 0 15px;
  white-space: nowrap;
  transition: all 150ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
  border-bottom: 2px solid transparent;
  &:hover {
    color: ${colors.primary};
    border-bottom-color: ${colors.primary};
  }
`;

const UserImageWrapper = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 15px;
  background-color: ${colors.lighterGray};
`;

const UserImage = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 15px;
  outline: none;
  border: none;
`;

const UserImagePlaceholder = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.gray};
  text-align: center;
`;

const UserDropdown = styled.div`
  transition: all 150ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
  position: absolute;
  top: 40px;
  right: 20px;
  padding: 10px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  border-top-left-radius: 5px;
  box-shadow: rgba(0, 0, 0, 0.08) 0 3px 10px 0;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;
  background-color: ${colors.white};
`;

const DropdownLink = styled(NavLink)`
  display: block;
  padding: 5px 10px 5px 10px;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.gray};
  text-decoration: none;
  text-align: center;
  border-radius: 10px;
  white-space: nowrap;
  transition: all 150ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
  &:hover {
    color: ${colors.primary};
  }
`;

const DropdownButton = styled.button`
  padding: 5px 10px 5px 10px;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.gray};
  text-decoration: none;
  border-radius: 10px;
  outline: none;
  border: none;
  display: block;
  width: 100%;
  white-space: nowrap;
  transition: all 150ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
  background: ${colors.white};
  &:hover {
    color: ${colors.primary};
  }
`;

const activeNavLinkStyle = {
  color: colors.primary,
  borderBottomColor: colors.primary
};

type Props = RouteComponentProps<{ chatId?: string; venueId?: string }> &
  ConnectedProps<typeof connector>;

const TopBar: React.FC<Props> = props => {
  const {
    loggedIn,
    image,
    userType,
    myVenues,
    isGettingMyVenues,
    userInitials
  } = props;

  const goHome = () => props.history.push("/");

  let Links = [];
  const UserLink = (
    <User key={`nav-link-user`} active={props.location.pathname === "/me"}>
      <UserImageWrapper>
        {image ? (
          <UserImage src={image} />
        ) : (
          <UserImagePlaceholder>{userInitials}</UserImagePlaceholder>
        )}
      </UserImageWrapper>
      <UserDropdown className="dropdown">
        <DropdownLink activeStyle={activeNavLinkStyle} to="/me">
          {userType === "USER" ? "Profile" : "Account"}
        </DropdownLink>
        <DropdownButton onClick={() => props.LogOut()}>Logout</DropdownButton>
      </UserDropdown>
    </User>
  );
  if (loggedIn && userType === "USER") {
    // normal user
    Links = [
      <StyledLink
        key={`nav-link-discover`}
        activeStyle={activeNavLinkStyle}
        to="/"
        exact={true}
      >
        Discover
      </StyledLink>,
      <StyledLink
        key={`nav-link-chat`}
        activeStyle={activeNavLinkStyle}
        to="/c"
        exact={true}
        isActive={(match, location) => {
          if (location.pathname.slice(0, 2) === "/c") return true;
          else return false;
        }}
      >
        Book
      </StyledLink>
    ];
  } else if (loggedIn && userType === "VENUE") {
    // venue
    Links = [
      <StyledLink
        key={`nav-link-dashboard`}
        activeStyle={activeNavLinkStyle}
        to="/"
        exact={true}
      >
        Dashboard
      </StyledLink>,
      <StyledLink
        key={`nav-link-listings`}
        activeStyle={activeNavLinkStyle}
        to={`/listings`}
        exact={true}
        isActive={(match, location) => {
          if (location.pathname.slice(0, 8) === "/listing") return true;
          else return false;
        }}
      >
        Listings
      </StyledLink>,
      <StyledLink
        key={`nav-link-chat`}
        activeStyle={activeNavLinkStyle}
        to="/c"
        exact={true}
        isActive={(match, location) => {
          if (location.pathname.slice(0, 2) === "/c") return true;
          else return false;
        }}
      >
        Inbox
      </StyledLink>
    ];
  } else if (loggedIn && userType === "ADMIN") {
    // venue
    Links = [
      <StyledLink
        key={`nav-link-dashboard`}
        activeStyle={activeNavLinkStyle}
        to="/"
        exact={true}
      >
        Dashboard
      </StyledLink>,
      <StyledLink
        key={`nav-link-listings`}
        activeStyle={activeNavLinkStyle}
        to={`/listings`}
        exact={true}
        isActive={(match, location) => {
          if (location.pathname.slice(0, 8) === "/listing") return true;
          else return false;
        }}
      >
        Venues
      </StyledLink>,
      <StyledLink
        key={`nav-link-chat`}
        activeStyle={activeNavLinkStyle}
        to="/c"
        exact={true}
        isActive={(match, location) => {
          if (location.pathname.slice(0, 2) === "/c") return true;
          else return false;
        }}
      >
        Support
      </StyledLink>
    ];
  } else {
    // no user logged in
    Links = [
      <StyledLink
        key={`nav-link-discover`}
        activeStyle={activeNavLinkStyle}
        to="/"
        exact={true}
      >
        Discover
      </StyledLink>,
      <StyledLink
        key={`nav-link-list-venue`}
        activeStyle={activeNavLinkStyle}
        to="/list"
        exact={true}
      >
        List Venue
      </StyledLink>,
      <StyledLink
        key={`nav-link-login`}
        activeStyle={activeNavLinkStyle}
        to="/login"
        exact={true}
      >
        Login
      </StyledLink>,
      <StyledLink
        key={`nav-link-join`}
        activeStyle={activeNavLinkStyle}
        to="/join"
        exact={true}
      >
        Join
      </StyledLink>
    ];
  }

  return (
    <React.Fragment>
      <Wrapper
        noBorder={
          props.location.pathname === "/" &&
          window.innerWidth < 600 &&
          userType !== "VENUE"
        }
      >
        {loggedIn && userType === "VENUE" && (
          <React.Fragment>
            <Left>
              <LogoWrapper onClick={goHome}>
                <LogoIcon size={30} />
              </LogoWrapper>
              <LinksWrapper>{Links}</LinksWrapper>
            </Left>
            <Right>
              {window.innerWidth > 600 && <CreateListing />}
              {UserLink}
              <OpenButton />
            </Right>
          </React.Fragment>
        )}
        {loggedIn && userType === "USER" && (
          <React.Fragment>
            <LogoWrapper onClick={goHome}>
              <LogoIcon size={30} />
            </LogoWrapper>
            <Search />
            <LinksWrapper>
              {Links}
              {UserLink}
            </LinksWrapper>
            <OpenButton />
          </React.Fragment>
        )}
        {loggedIn && userType === "ADMIN" && (
          <React.Fragment>
            <Left>
              <LogoWrapper onClick={goHome}>
                <LogoIcon size={30} />
              </LogoWrapper>
              <LinksWrapper>{Links}</LinksWrapper>
            </Left>
            <Right>
              {window.innerWidth > 600 && <CreateListing />}
              {UserLink}
              <OpenButton />
            </Right>
          </React.Fragment>
        )}
        {(!loggedIn || (loggedIn && !userType)) && (
          <React.Fragment>
            <LogoWrapper onClick={goHome}>
              <LogoIcon size={30} />
            </LogoWrapper>
            <Search />
            <LinksWrapper>{Links}</LinksWrapper>
            <OpenButton />
          </React.Fragment>
        )}
      </Wrapper>
      <MobileMenu />
    </React.Fragment>
  );
};

const mapState = (state: RootState, ownProps: RouteComponentProps) => ({
  loggedIn: selectors.loggedIn(state),
  myVenues: selectors.myVenues(state),
  image: selectors.userImage(state),
  userInitials: selectors.userInitials(state),
  userType: selectors.userType(state),
  isGettingMyVenues: selectors.isGettingMyVenues(state)
});
const mapDispatch = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      LogOut
    },
    dispatch
  );
const connector = connect(mapState, mapDispatch);

export default withRouter(connector(TopBar));
